import {
  addressUI,
  addressSchema,
} from 'platform/forms-system/src/js/web-component-patterns/addressPattern';

import {
  textUI,
  textSchema,
  currentOrPastMonthYearDateSchema,
  currentOrPastMonthYearDateUI,
  currentOrPastMonthYearDateRangeUI,
  titleUI,
  radioUI,
  radioSchema,
} from 'platform/forms-system/src/js/web-component-patterns';

import { HospitalView, DateRangeView } from '../components/viewElements';
import { wrapRadioUiWithDl } from '../helpers/reviewHelpers';
import SafeArrayField from '../components/SafeArrayField';
import ConnectedDisabilitiesField from '../components/ConnectedDisabilitiesField';
import { extractDisabilityLabels } from '../helpers/disabilityOptions';

import { validateDateRange } from '../helpers/dateRangeCustom';
import { withDobValidation } from '../helpers/dobValidation';

const hospitalTypeLabels = {
  va: 'VA Hospital',
  nonVa: 'Non-VA Hospital',
};

const DEFAULT_DL_TITLES = {
  city: 'City',
  state: 'State',
};

const addUseDlWrap = (field, key) => {
  if (!field) {
    return field;
  }

  const updatedField = {
    ...field,
    'ui:options': {
      ...field['ui:options'],
      useDlWrap: true,
    },
  };

  if (key && DEFAULT_DL_TITLES[key] && !updatedField['ui:title']) {
    updatedField['ui:title'] = DEFAULT_DL_TITLES[key];
  }

  return updatedField;
};

const addressWithDlWrap = uiSchema =>
  Object.keys(uiSchema).reduce((acc, key) => {
    const value = uiSchema[key];
    if (key.startsWith('ui:') || key.startsWith('view:')) {
      acc[key] = value;
      return acc;
    }

    acc[key] = addUseDlWrap(value, key);
    return acc;
  }, {});

const shouldUseDateRange = fullData => !!fullData?.form218940DateValidation;

const hideOldDateFields = (_formData, _index, fullData) =>
  shouldUseDateRange(fullData);

const hideNewDateFields = (_formData, _index, fullData) =>
  !shouldUseDateRange(fullData);

const updateTreatmentDateSchema = (
  _formData,
  schema,
  _uiSchema,
  _index,
  _path,
  fullData,
) => {
  const requiredField = shouldUseDateRange(fullData) ? 'from' : 'startDate';

  const updateItemSchema = item => {
    if (!item?.properties) {
      return item;
    }

    return {
      ...item,
      required: [requiredField],
    };
  };

  if (Array.isArray(schema?.items)) {
    const updatedItems = schema.items.map((item, index) =>
      index === 0 ? updateItemSchema(item) : item,
    );
    const result = { items: updatedItems };
    if (schema.additionalItems && typeof schema.additionalItems === 'object') {
      result.additionalItems = updateItemSchema(schema.additionalItems);
    }
    return result;
  }

  return {
    items: updateItemSchema(schema?.items),
  };
};

const treatmentDateSchema = {
  type: 'object',
  properties: {
    startDate: currentOrPastMonthYearDateSchema,
    endDate: currentOrPastMonthYearDateSchema,
    from: currentOrPastMonthYearDateSchema,
    to: currentOrPastMonthYearDateSchema,
  },
};

const treatmentDateRangeUi = currentOrPastMonthYearDateRangeUI(
  {
    title: 'Start date of hospitalization',
    hint: 'For example: January 2022',
  },
  {
    title: 'End date of hospitalization (if applicable)',
    hint: 'For example: January 2022',
  },
);

const START_DATE_DOB_ERROR =
  "Enter a valid start date. The start date of hospitalization cannot be on or before the Veteran's date of birth.";
const END_DATE_DOB_ERROR =
  "Enter a valid end date. The end date of hospitalization cannot be on or before the Veteran's date of birth.";

const treatmentStartDateUi = currentOrPastMonthYearDateUI({
  title: 'Start date of hospitalization',
  hint: 'For example: January 2022',
});

const treatmentEndDateUi = currentOrPastMonthYearDateUI({
  title: 'End date of hospitalization (if applicable)',
  hint: 'For example: January 2022',
});

const hospitalItemSchema = {
  type: 'object',
  properties: {
    hospitalType: radioSchema(Object.keys(hospitalTypeLabels)),
    hospitalName: textSchema,
    hospitalAddress: addressSchema({ omit: ['street2', 'street3'] }),
    connectedDisabilities: {
      type: 'array',
      minItems: 1,
      uniqueItems: true,
      items: {
        type: 'string',
        enum: [],
      },
    },
    treatmentDates: {
      type: 'array',
      minItems: 0,
      maxItems: 10,
      items: [treatmentDateSchema],
      additionalItems: treatmentDateSchema,
    },
  },
  required: [
    'hospitalType',
    'hospitalName',
    'hospitalAddress',
    'connectedDisabilities',
  ],
};

/** @type {PageSchema} */
export default {
  uiSchema: {
    ...titleUI('Hospital information'),
    hospitals: {
      'ui:field': SafeArrayField,
      'ui:options': {
        itemName: 'Hospital',
        viewField: HospitalView,
        customTitle: ' ',
        useDlWrap: true,
        keepInPageOnReview: true,
        doNotScroll: true,
        confirmRemove: true,
        confirmRemoveDescription:
          'Are you sure you want to remove this hospital?',
        itemAriaLabel: item => `hospital: ${item?.hospitalName || 'Hospital'}`,
        updateSchema: (
          _fieldData,
          schema,
          _uiSchema,
          _index,
          _path,
          fullData,
        ) => {
          const disabilityOptions = extractDisabilityLabels(fullData);

          const schemaItems = Array.isArray(schema?.items)
            ? schema.items[0]
            : schema?.items;
          const connectedSchema =
            schemaItems?.properties?.connectedDisabilities;

          if (!connectedSchema) {
            return {};
          }

          const existing = connectedSchema;
          const hasOptions = disabilityOptions.length > 0;
          const updatedConnected = {
            ...existing,
            // Only require a selection when at least one disability is available
            minItems: hasOptions ? 1 : 0,
            items: {
              ...(existing.items || { type: 'string' }),
              enum: disabilityOptions,
            },
          };

          if (Array.isArray(updatedConnected.enum)) {
            delete updatedConnected.enum;
          }

          const sanitizedConnected = { ...updatedConnected };
          if ('additionalItems' in sanitizedConnected) {
            delete sanitizedConnected.additionalItems;
          }
          if (
            sanitizedConnected.items &&
            typeof sanitizedConnected.items === 'object'
          ) {
            const sanitizedItems = { ...sanitizedConnected.items };
            if (!sanitizedItems.type) {
              sanitizedItems.type = 'string';
            }
            if ('additionalItems' in sanitizedItems) {
              delete sanitizedItems.additionalItems;
            }
            sanitizedConnected.items = sanitizedItems;
          }

          const adjustRequired = requiredList => {
            if (!Array.isArray(requiredList)) {
              return requiredList;
            }
            if (hasOptions) {
              return requiredList.includes('connectedDisabilities')
                ? requiredList
                : [...requiredList, 'connectedDisabilities'];
            }
            const filtered = requiredList.filter(
              field => field !== 'connectedDisabilities',
            );
            return filtered.length ? filtered : undefined;
          };

          const updateItemSchema = item => {
            if (!item?.properties?.connectedDisabilities) {
              return item;
            }
            const adjustedRequired = adjustRequired(item?.required);
            const updatedItem = {
              ...item,
              properties: {
                ...item.properties,
                connectedDisabilities: sanitizedConnected,
              },
            };
            if (adjustedRequired === undefined) {
              delete updatedItem.required;
            } else {
              updatedItem.required = adjustedRequired;
            }
            return updatedItem;
          };

          if (Array.isArray(schema?.items)) {
            const updatedItems = schema.items.map((item, index) =>
              index === 0 ? updateItemSchema(item) : item,
            );
            const result = { items: updatedItems };
            if (
              schema.additionalItems &&
              typeof schema.additionalItems === 'object'
            ) {
              result.additionalItems = updateItemSchema(schema.additionalItems);
            }
            return result;
          }

          return {
            items: updateItemSchema(schemaItems),
          };
        },
      },
      items: {
        'ui:options': {
          classNames: 'vads-u-margin-left--1p5',
        },
        hospitalType: wrapRadioUiWithDl(
          radioUI({
            title: 'Which type of hospital provided your care?',
            labels: hospitalTypeLabels,
            required: () => true,
            errorMessages: {
              required: 'Please select if this is a VA or Non-VA hospital.',
            },
          }),
        ),
        hospitalName: textUI({
          title: 'Hospital name',
          useDlWrap: true,
          required: () => true,
          errorMessages: {
            required: 'Enter the hospital name',
          },
        }),
        hospitalAddress: {
          ...addressWithDlWrap(
            addressUI({
              labels: {
                militaryCheckbox: 'Hospital is on a military base',
              },
              omit: ['street2', 'street3'],
            }),
          ),
          required: () => true,
        },
        connectedDisabilities: {
          'ui:title':
            'Please select your service-connected disabilities for this hospitalization.',
          'ui:field': ConnectedDisabilitiesField,
          'ui:required': () => true,
          'ui:errorMessages': {
            minItems:
              'Select at least one service-connected disability for this hospitalization',
          },
        },
        treatmentDates: {
          'ui:field': SafeArrayField,
          'ui:options': {
            itemName: 'Hospitalization Date',
            viewField: DateRangeView,
            customTitle: 'Your important hospitalization dates',
            useDlWrap: true,
            keepInPageOnReview: true,
            doNotScroll: true,
            confirmRemove: true,
            confirmRemoveDescription:
              'Are you sure you want to remove this hospitalization date?',
            addAnotherText: 'Add another hospitalization date',
            itemAriaLabel: ({ from, to, startDate, endDate } = {}) =>
              `Hospitalization Date: ${[from || startDate, to || endDate]
                .filter(Boolean)
                .join(' to ')}`,
            updateSchema: updateTreatmentDateSchema,
          },
          items: {
            'ui:options': {
              classNames: 'vads-u-margin-left--1p5',
            },
            ...treatmentDateRangeUi,
            'ui:validations': [
              {
                validator: (errors, fieldData, formData, schema, uiSchema) => {
                  validateDateRange(
                    errors,
                    fieldData,
                    formData,
                    schema,
                    uiSchema,
                    true,
                  );
                },
              },
            ],

            from: {
              ...withDobValidation(
                treatmentDateRangeUi.from,
                START_DATE_DOB_ERROR,
              ),
              'ui:options': {
                ...treatmentDateRangeUi.from?.['ui:options'],
                hideIf: hideNewDateFields,
              },
            },
            to: {
              ...withDobValidation(treatmentDateRangeUi.to, END_DATE_DOB_ERROR),
              'ui:options': {
                ...treatmentDateRangeUi.to?.['ui:options'],
                hideIf: hideNewDateFields,
              },
            },
            startDate: {
              ...withDobValidation(treatmentStartDateUi, START_DATE_DOB_ERROR),
              'ui:options': {
                ...treatmentStartDateUi['ui:options'],
                hideIf: hideOldDateFields,
              },
            },
            endDate: {
              ...withDobValidation(treatmentEndDateUi, END_DATE_DOB_ERROR),
              'ui:options': {
                ...treatmentEndDateUi['ui:options'],
                hideIf: hideOldDateFields,
              },
            },
          },
        },
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      hospitals: {
        type: 'array',
        minItems: 0,
        maxItems: 10,
        items: [hospitalItemSchema],
        additionalItems: hospitalItemSchema,
      },
    },
  },
};
